/**
 * O rateio corporativo do checkout: os tipos que o `AllocationField` lê e as
 * regras PURAS de centro de custo, compartilhadas com o envio do genui-kit (que
 * ecoa o `{id, name}` escolhido). Tipos próprios e estruturais: o form
 * corporativo do genui-kit cabe neles sem conversão.
 */

/** Uma opção corporativa (centro de custo, projeto) como o catálogo a traz. */
export interface AllocationOption {
  readonly id: string;
  readonly name: string;
  readonly code?: string;
}

export interface AllocationCompany {
  readonly id: string;
  readonly name: string;
  /** O catálogo de centros de custo não é exaustivo: pergunte ao servidor. */
  readonly allCostCenters?: boolean;
  readonly costCenters: readonly AllocationOption[];
  readonly projects: readonly AllocationOption[];
}

/** O catálogo que o campo lê: empresas e o interruptor de projeto do cliente. */
export interface AllocationCatalog {
  readonly companies: readonly AllocationCompany[];
  readonly enableProjectField?: boolean;
}

/** A escolha em edição. `costCenterOption` é o eco da escolha vinda da BUSCA
 *  remota, que não está no catálogo por definição. */
export interface AllocationDraft {
  companyId: string;
  costCenterId: string;
  projectId: string;
  costCenterOption?: AllocationOption;
  /** A parte desta linha num rateio de várias linhas (#6697). Ausente numa linha
   *  sozinha, que vale 100%. */
  percent?: number;
}

/**
 * Rótulo exibível de um centro de custo: `code - name`, com as partes vazias
 * descartadas. Paridade com o app real (`MotiveCostCenter.label`), que junta
 * `[code, name].where(isNotEmpty)`.
 *
 * Existe porque o cadastro do cliente pode pôr a identidade do item no CÓDIGO e
 * deixar o `name` em branco — nesse caso o rótulo era string vazia e a opção
 * renderizava INVISÍVEL na lista (#2459).
 */
export function costCenterLabel(option: AllocationOption): string {
  return [option.code, option.name].filter(Boolean).join(' - ');
}

/**
 * Resolve o centro de custo escolhido para `{id, name, code?}`.
 *
 * Duas fontes, nesta ordem: o catálogo embutido, e — quando a escolha veio da
 * BUSCA remota — o eco guardado no próprio draft. Sem o eco, tudo que depende do
 * nome (resumo da alocação, farol de orçamento, eco do envio) ficaria vazio para
 * a empresa cujo catálogo não é exaustivo.
 */
export function resolveCostCenter(
  catalog: AllocationCatalog,
  allocation: AllocationDraft,
): AllocationOption | undefined {
  if (!allocation.costCenterId) return undefined;
  const company = catalog.companies.find((c) => c.id === allocation.companyId);
  const known = company?.costCenters.find((cc) => cc.id === allocation.costCenterId);
  if (known) return known;
  // O eco só vale para a escolha ATUAL: trocar de empresa/centro sem limpá-lo
  // devolveria o rótulo da escolha anterior.
  const echo = allocation.costCenterOption;
  return echo?.id === allocation.costCenterId ? echo : undefined;
}

/**
 * Se o campo de centro de custo deve BUSCAR no servidor em vez de listar o
 * catálogo embutido.
 *
 * A regra é a do app real (`GetCostsCenter`): `allCostCenters` ligado — ou lista
 * vazia — significa "o catálogo não é exaustivo, pergunte ao servidor", nunca
 * "não há opção". Sem a busca (preview, Storybook, host legado) o campo degrada
 * para o select de sempre.
 */
export function shouldSearchCostCenters(
  company: AllocationCompany | undefined,
  canSearch: boolean,
): boolean {
  if (!company || !canSearch) return false;
  return company.allCostCenters === true || company.costCenters.length === 0;
}
